import { Box, Flex, Skeleton, Text } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import React, { useEffect, useMemo, useState } from 'react'
import CallIcon from '~/assets/svgs/icon-call-start.svg?react'
import DownloadIcon from '~/assets/svgs/icon-download.svg?react'
import EmailIcon from '~/assets/svgs/icon-email.svg?react'
import FeedbackIcon from '~/assets/svgs/icon-feedback-message.svg?react'
import LogoutIcon from '~/assets/svgs/icon-logout.svg?react'
import MangoLogo from '~/assets/svgs/icon-mango-logo.svg?react'
import SwapIcon from '~/assets/svgs/icon-swap-arrows.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import {
  DropdownTextButton,
  TextButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { InitialsAvatar } from '~/components/shared/InitialsAvatar'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { TextButton } from '~/components/shared/TextButton'
import urls from '~/constants/urls'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useEmitterService } from '~/hooks/useEmitterService'
import { useLogout } from '~/hooks/useLogout'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { desktopAppDetectionBannerStateAtom } from '~/pages/Home/Banners/DesktopAppDetectionBanner'
import { useProfileSettingsStyle } from '~/pages/Settings/componentStyles/ProfileSettings.style'
import { useSharedSettingsStyle } from '~/pages/Settings/componentStyles/SharedSettings.style'
import { SettingToggle } from '~/pages/Settings/SettingItems'
import { useGetPbxList } from '~/queries/pbxQueries'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import Logger from '~/services/Logger'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

import { Pbx } from '@mangovoice/types'

const logger = new Logger('ProfileSettings')

// Key for storing that the user does not want to see the confirmation modal again when switching extensions
const SWITCH_PBX_CONFIRMATION_KEY = 'switch-pbx'

export const ProfileSettings = () => {
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const styles = useProfileSettingsStyle()
  const sharedStyles = useSharedSettingsStyle()
  const handleLogout = useLogout()
  const { pbx, getUserSessionDependencies, isErrored, sessionUser } =
    useUserState()
  const {
    confirmModal: [, setConfirmModalState],
    callInProgressModal: [, setCallInProgressModalState],
  } = useModalState()
  const [expectedPbxId, setExpectedPbxId] = useState<number | undefined>()
  const { emit } = useEmitterService()
  const [desktopAppDetectionBannerState, setDesktopAppDetectionBannerState] =
    useAtom(desktopAppDetectionBannerStateAtom)

  const { data: pbxList } = useGetPbxList({})

  const {
    state: {
      phoneCalls: { list },
    },
  } = usePhoneEndpoint({})

  const handleCallSupport = () => {
    openInNewTab('tel:4357670882')
  }

  const handleEmailSupport = () => {
    openInNewTab('mailto:support@mangovoice.com')
  }

  const handleSendFeedback = () => {
    openInNewTab(urls.FEEDBACK)
  }

  const handleDownloadApp = () => {
    openInNewTab(urls.DESKTOP_APP_DOWNLOAD)
  }

  const handleLaunchApp = () => {
    openInNewTab('mango://settings')
  }

  const hasOpenCall = list.callList.size > 0

  const handleMenuOpenChange = () => {
    if (hasOpenCall) {
      setCallInProgressModalState({ open: true })
    }
    return !hasOpenCall
  }

  const handleSwitchPbx = (value: Pbx) => {
    if (pbx?.id === (value as Pbx).id) {
      return
    }

    logger.info(`Attempting to switch to pbx ${value}`)

    if (hasOpenCall) {
      logger.info(`Cannot switch pbx's while a call is in progress`)
      setCallInProgressModalState({ open: true })
    } else {
      setConfirmModalState({
        title: `Change Your Current PBX`,
        message: (
          <Box>
            <Text sx={styles.modalBodyText}>
              Calls, texts, faxes, and voicemails from {pbx?.name} will not be
              available on this application until you switch back.
              {'\u00A0'}
            </Text>
          </Box>
        ),
        confirmText: 'Ok',
        cancelText: 'Cancel',
        closeAfterConfirm: true,
        isDestructive: false,
        dontShowAgainKey: SWITCH_PBX_CONFIRMATION_KEY,
        onConfirm: () => {
          logger.info(`Confirmed switch to pbx, switching now...`)
          track(AuthenticationAnalyticsEvents.PbxSwitched)
          setExpectedPbxId(value.id)
          getUserSessionDependencies(value as Pbx)
        },
      })
    }
  }

  useEffect(() => {
    if (!expectedPbxId) {
      return
    }

    if (expectedPbxId === pbx?.id) {
      // Show a toast that the pbx has been switched, then clear the state we were watching for
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.GENERAL,
        operation: GeneralOperation.SwitchedPbx,
        message: pbx?.name,
      })
      setExpectedPbxId(undefined)
    }
  }, [expectedPbxId, pbx])

  useEffect(() => {
    if (expectedPbxId && isErrored) {
      // We failed to switch extensions, clear the expected extension id
      setExpectedPbxId(undefined)
    }
  }, [expectedPbxId, isErrored])

  const pbxMenuItems = useMemo(() => {
    return (
      pbxList?.results.map((pbx) => {
        return {
          content: <MenuListItem key={pbx.id} primaryText={pbx.name} />,
          value: pbx,
        } as TextButtonMenuItem<string | number | object>
      }) ?? []
    )
  }, [pbxList])

  return (
    <Flex sx={sharedStyles.wrapper}>
      <Flex sx={styles.wrapper}>
        <Flex sx={styles.profileCard}>
          <Flex sx={styles.nameWrapper}>
            <InitialsAvatar
              firstName={sessionUser?.first_name}
              lastName={sessionUser?.last_name}
              size="lg"
            />
            <Text sx={styles.nameText}>
              {sessionUser?.first_name} {sessionUser?.last_name}
            </Text>
          </Flex>
          <Flex sx={styles.headerPbx}>
            <Text sx={{ fontSize: '.75rem', fontWeight: '400' }}>PBX Name</Text>
            <Text sx={styles.headerPbxText}>{pbx?.name}</Text>
            {(pbxList?.results.length ?? 0) > 1 && (
              <DropdownTextButton
                placeholder="Switch PBX"
                items={pbxMenuItems as TextButtonMenuItem<Pbx>[]}
                leftIcon={SwapIcon}
                onSelect={handleSwitchPbx}
                onMenuOpenChange={handleMenuOpenChange}
                menuOptions={{
                  width: '25rem',
                  placement: MenuPlacement.BottomCenter,
                }}
              />
            )}
          </Flex>
          {!isElectron && (
            <Flex sx={styles.helpSection}>
              <Flex sx={styles.helpTitleWrapper}>
                <Text sx={styles.helpTitleText}>Desktop App</Text>
                <NotificationBadge
                  count={'Beta'}
                  size={Size.md}
                  variant={NotificationBadgeVariant.Beta}
                  sx={{
                    paddingLeft: '.5rem',
                  }}
                />
              </Flex>
              <SettingToggle
                label={'Prompt to use the Desktop App'}
                isChecked={
                  !Object.prototype.hasOwnProperty.call(
                    desktopAppDetectionBannerState,
                    'ignoreDesktopApp',
                  ) || desktopAppDetectionBannerState.ignoreDesktopApp === false
                }
                onClick={() => {
                  setDesktopAppDetectionBannerState({
                    ...desktopAppDetectionBannerState,
                    ignoreDesktopApp:
                      !desktopAppDetectionBannerState.ignoreDesktopApp,
                  })
                }}
              />
              <TextButton leftIcon={MangoLogo} onLinkClick={handleLaunchApp}>
                Open Desktop App
              </TextButton>
              <TextButton
                leftIcon={DownloadIcon}
                onLinkClick={handleDownloadApp}
              >
                Download Desktop App
              </TextButton>
            </Flex>
          )}
          <Flex sx={styles.helpSection}>
            <Flex sx={styles.helpTitleWrapper}>
              <Text sx={styles.helpTitleText}>Need Help?</Text>
            </Flex>
            <TextButton leftIcon={CallIcon} onLinkClick={handleCallSupport}>
              Call support (435) 767-0882
            </TextButton>
            <TextButton leftIcon={EmailIcon} onLinkClick={handleEmailSupport}>
              Email support
            </TextButton>
            <TextButton
              leftIcon={FeedbackIcon}
              onLinkClick={handleSendFeedback}
            >
              Send Feedback
            </TextButton>
          </Flex>
          <Flex sx={styles.headerActions}>
            <TextButton leftIcon={LogoutIcon} onLinkClick={handleLogout}>
              Log Out
            </TextButton>
          </Flex>
        </Flex>
        {expectedPbxId && (
          <Box sx={styles.skeletonContainer}>
            <Skeleton sx={styles.skeleton} />
          </Box>
        )}
      </Flex>
    </Flex>
  )
}
